import { Grid } from '@material-ui/core';
import { useQuoteField } from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import { FormRowGrid, GridRowProps } from 'Quoting/components/shared/GridRow';
import { ConditionalSection, QuoteFormConditionalSection } from 'Quoting/components/shared/QuoteFormConditionalSection';
import { QuoteFormControlElement } from 'Quoting/components/shared/QuoteFormControlElement';
import { QuoteFormElement } from 'Quoting/components/shared/QuoteFormElement';
import { FormWrapper } from 'Quoting/components/shared/QuoteFormWrapper';
import { getBenefitAmount } from 'Quoting/utils/utilities';
import { default as React, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReducerState } from 'rootReducer';
import { getCalculatedBenefitAmounts } from '../../PlanTemplates/ACC/GAI7';
import {
  burnDegreeBenefitAmount,
  fractureDefaultValues,
  handleRestrictPlanOptions,
  openAbdominalOrThoracicSurgeryDetailsBenefitAmount,
  reductionDetailsDefaults,
  unitsOptions,
} from '../../PlanTemplates/ACC/benefitDefaults';
import { AccidentFollowUpTreatment } from './AccidentFollowUpTreatment';
import { BrainInjuryDiagnosis } from './BrainInjuryDiagnosis';
import { BrokenTooth } from './BrokenTooth';
import { BurnDetails } from './BurnDetails';
import { BurnSkinGraft } from './BurnSkinGraft';
import { ComaDetails } from './ComaDetails';
import { DailyHospitalConfinement } from './DailyHospitalConfinement';
import { GunshotWound } from './GunshotWound';
import { InitialHospitalAdmission } from './InitialHospitalAdmission';
import { IntensiveCareUnitConfinement } from './IntensiveCareUnitConfinement';
import { LacerationDetails } from './LacerationDetails';
import { ParalysisDetails } from './ParalysisDetails';
import { accidentFacilityCarePath } from './PropertyPaths/accidentFacilityCarePath';
import { accidentInjuriesPath } from './PropertyPaths/accidentInjuriesPath';
import {
  accidentBenefitsUpdateFields,
  accidentFollowupTreatmentPath,
  getAccidentXRayPath,
} from './PropertyPaths/propertyPathACC';
import { PunctureWound } from './PunctureWound';
import { RehabilitationFacility } from './RehabilitationFacility';
import { StepDownICUConfinement } from './StepDownICUConfinement';
import { List as ImmutableList } from 'immutable';

export const AccidentFacilityCareBlock = () => {
  const { getQuoteFieldValue, updateQuoteFieldValues } = useQuoteField();
  const { selectedPlan, defaultPlanValues } = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentFacilityCarePath(selectedPlan?.planIndex ?? 0, 0);

  const accidentBenefitsUpdateUnitsPath = accidentBenefitsUpdateFields(selectedPlan?.planIndex ?? 0, 0);

  const lacerationBenefit = getBenefitAmount(defaultPlanValues, 'ACCLacerationBenefit.standardBenefitAmount');
  const accidentalDismembermentBenefit = getBenefitAmount(
    defaultPlanValues,
    'ACCAccidentalDismembermentBenefit.standardBenefitAmount'
  );

  const functionalLossBenefitAmount = getBenefitAmount(
    defaultPlanValues,
    'ACCFunctionalLossBenefit.standardBenefitAmount'
  );
  const paralysisBenefitAmount = getBenefitAmount(defaultPlanValues, 'ACCParalysisBenefit.standardBenefitAmount');

  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');
  const [planUnitsOptions, setPlanUnitsOptions] = useState<any[]>([]);
  const numOfPlans = getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans')?.size || 0;
  const redInd1 = 1;
  const redInd2 = 0;

  const dislocationFieldUpdate = (units: any) => {
    const openReductionDetailsPath = accidentBenefitsUpdateUnitsPath.reductionAmountDetails[redInd2];
    const closedReductionDetailsPath = accidentBenefitsUpdateUnitsPath.reductionAmountDetails[redInd1];
    return [
      // New Reduction Amount Updates (open and closed reduction)
      {
        path: openReductionDetailsPath.hipAmount,
        newValue: 3 * reductionDetailsDefaults.hipStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.kneeNoPatellaAmount,
        newValue: 3 * reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.footBonesAmount,
        newValue: 3 * reductionDetailsDefaults.footBonesStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.ankleAmount,
        newValue: 3 * reductionDetailsDefaults.ankleStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.wristAmount,
        newValue: 3 * reductionDetailsDefaults.wristStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.elbowAmount,
        newValue: 3 * reductionDetailsDefaults.elbowStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.shoulderGlenohumeralAmount,
        newValue: 3 * reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.handBonesAmount,
        newValue: 3 * reductionDetailsDefaults.handBonesStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.collarboneAcromioclavicularAmount,
        newValue: 3 * reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.twoOrMoreFingersAmount,
        newValue: 3 * reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.twoOrMoreToesAmount,
        newValue: 3 * reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.oneFingerOrToeAmount,
        newValue: 3 * reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.kneecapAmount,
        newValue: 3 * reductionDetailsDefaults.kneecapStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.lowerJawAmount,
        newValue: 3 * reductionDetailsDefaults.lowerJawStandardBenefitAmount * units,
      },
      {
        path: openReductionDetailsPath.otherDislocationAmount,
        newValue: 3 * reductionDetailsDefaults.otherDislocationStandardBenefitAmount * units,
      },

      { path: closedReductionDetailsPath.hipAmount, newValue: reductionDetailsDefaults.hipStandardBenefitAmount * units },
      {
        path: closedReductionDetailsPath.kneeNoPatellaAmount,
        newValue: reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.footBonesAmount,
        newValue: reductionDetailsDefaults.footBonesStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.ankleAmount,
        newValue: reductionDetailsDefaults.ankleStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.wristAmount,
        newValue: reductionDetailsDefaults.wristStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.elbowAmount,
        newValue: reductionDetailsDefaults.elbowStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.shoulderGlenohumeralAmount,
        newValue: reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.handBonesAmount,
        newValue: reductionDetailsDefaults.handBonesStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.collarboneAcromioclavicularAmount,
        newValue: reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.twoOrMoreFingersAmount,
        newValue: reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.twoOrMoreToesAmount,
        newValue: reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.oneFingerOrToeAmount,
        newValue: reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.kneecapAmount,
        newValue: reductionDetailsDefaults.kneecapStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.lowerJawAmount,
        newValue: reductionDetailsDefaults.lowerJawStandardBenefitAmount * units,
      },
      {
        path: closedReductionDetailsPath.otherDislocationAmount,
        newValue: reductionDetailsDefaults.otherDislocationStandardBenefitAmount * units,
      },
    ];
  };
  const fractureDislocationFieldUpdate = (units: any) => {
    const openFractureDetailsPath = accidentBenefitsUpdateUnitsPath.fractureDetailsReductionAmountDetails[redInd2];
    const closedFractureDetailsPath = accidentBenefitsUpdateUnitsPath.fractureDetailsReductionAmountDetails[redInd1];

    return [
      // Open Fracture Amount Updates
      {
        path: openFractureDetailsPath.hipAmount,
        newValue: 3 * fractureDefaultValues.hipThighFemurFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.skullFractureNonDepressedNoFaceNoseBonesAmount,
        newValue: 3 * fractureDefaultValues.skullFractureNonDepressedAmount * units,
      },
      {
        path: openFractureDetailsPath.pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount,
        newValue: 3 * fractureDefaultValues.pelvisFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.upperArmBetwElbowAndShoulderHumerusAmount,
        newValue: 3 * fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.shoulderBladeScapulaAmount,
        newValue: 3 * fractureDefaultValues.shoulderBladeFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.legTibiaFibulaAmount,
        newValue: 3 * fractureDefaultValues.legFractureAmount * units,
      },
      { path: openFractureDetailsPath.ankleAmount, newValue: 3 * fractureDefaultValues.ankleFractureAmount * units },
      {
        path: openFractureDetailsPath.kneecapPatellaAmount,
        newValue: 3 * fractureDefaultValues.kneecapFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.collarboneClavicleSternumAmount,
        newValue: 3 * fractureDefaultValues.collarBoneFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.forearmRadiusUlnaAmount,
        newValue: 3 * fractureDefaultValues.forearmFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.footOrHeelNoToesAmount,
        newValue: 3 * fractureDefaultValues.footFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.handNoFingersAmount,
        newValue: 3 * fractureDefaultValues.handOrWristFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.lowerJawMandibleNoAlveolarProcessAmount,
        newValue: 3 * fractureDefaultValues.lowerJawFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.twoOrMoreRibsFingersOrToesAmount,
        newValue: 3 * fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.faceOrNoseBonesNoMandibleOrMaxillaAmount,
        newValue: 3 * fractureDefaultValues.bonesOfFaceFractureAmount * units,
      },
      { path: openFractureDetailsPath.noseAmount, newValue: 3 * fractureDefaultValues.noseFractureAmount * units },
      {
        path: openFractureDetailsPath.oneRibFingerOrToeAmount,
        newValue: 3 * fractureDefaultValues.oneRibFingerToeFractureAmount * units,
      },
      { path: openFractureDetailsPath.coccyxAmount, newValue: 3 * fractureDefaultValues.coccyxFractureAmount * units },
      {
        path: openFractureDetailsPath.skullFractureDepressedNoFaceNoseBonesAmount,
        newValue: 3 * fractureDefaultValues.skullFractureDepressedAmount * units,
      },
      {
        path: openFractureDetailsPath.vertebraeBodyOfNoVertebralProcessesAmount,
        newValue: 3 * fractureDefaultValues.vertebralBodyFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.vertebralProcessesAmount,
        newValue: 3 * fractureDefaultValues.vertebralProcessFractureAmount * units,
      },
      {
        path: openFractureDetailsPath.upperJawMaxillaNoAlveolarProcessAmount,
        newValue: 3 * fractureDefaultValues.upperJawFractureAmount * units,
      },
      { path: openFractureDetailsPath.sternumAmount, newValue: 3 * fractureDefaultValues.sternumFractureAmount * units },
      {
        path: openFractureDetailsPath.anyOtherFractureAmount,
        newValue: 3 * fractureDefaultValues.anyOtherFractureAmount * units,
      },

      // Closed Fracture Amount Updates
      { path: closedFractureDetailsPath.hipAmount, newValue: fractureDefaultValues.hipThighFemurFractureAmount * units },
      {
        path: closedFractureDetailsPath.skullFractureNonDepressedNoFaceNoseBonesAmount,
        newValue: fractureDefaultValues.skullFractureNonDepressedAmount * units,
      },
      {
        path: closedFractureDetailsPath.pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount,
        newValue: fractureDefaultValues.pelvisFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.upperArmBetwElbowAndShoulderHumerusAmount,
        newValue: fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.shoulderBladeScapulaAmount,
        newValue: fractureDefaultValues.shoulderBladeFractureAmount * units,
      },
      { path: closedFractureDetailsPath.legTibiaFibulaAmount, newValue: fractureDefaultValues.legFractureAmount * units },
      { path: closedFractureDetailsPath.ankleAmount, newValue: fractureDefaultValues.ankleFractureAmount * units },
      {
        path: closedFractureDetailsPath.kneecapPatellaAmount,
        newValue: fractureDefaultValues.kneecapFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.collarboneClavicleSternumAmount,
        newValue: fractureDefaultValues.collarBoneFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.forearmRadiusUlnaAmount,
        newValue: fractureDefaultValues.forearmFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.footOrHeelNoToesAmount,
        newValue: fractureDefaultValues.footFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.handNoFingersAmount,
        newValue: fractureDefaultValues.handOrWristFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.lowerJawMandibleNoAlveolarProcessAmount,
        newValue: fractureDefaultValues.lowerJawFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.twoOrMoreRibsFingersOrToesAmount,
        newValue: fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.faceOrNoseBonesNoMandibleOrMaxillaAmount,
        newValue: fractureDefaultValues.bonesOfFaceFractureAmount * units,
      },
      { path: closedFractureDetailsPath.noseAmount, newValue: fractureDefaultValues.noseFractureAmount * units },
      {
        path: closedFractureDetailsPath.oneRibFingerOrToeAmount,
        newValue: fractureDefaultValues.oneRibFingerToeFractureAmount * units,
      },
      { path: closedFractureDetailsPath.coccyxAmount, newValue: fractureDefaultValues.coccyxFractureAmount * units },
      {
        path: closedFractureDetailsPath.skullFractureDepressedNoFaceNoseBonesAmount,
        newValue: fractureDefaultValues.skullFractureDepressedAmount * units,
      },
      {
        path: closedFractureDetailsPath.vertebraeBodyOfNoVertebralProcessesAmount,
        newValue: fractureDefaultValues.vertebralBodyFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.vertebralProcessesAmount,
        newValue: fractureDefaultValues.vertebralProcessFractureAmount * units,
      },
      {
        path: closedFractureDetailsPath.upperJawMaxillaNoAlveolarProcessAmount,
        newValue: fractureDefaultValues.upperJawFractureAmount * units,
      },
      { path: closedFractureDetailsPath.sternumAmount, newValue: fractureDefaultValues.sternumFractureAmount * units },
      {
        path: closedFractureDetailsPath.anyOtherFractureAmount,
        newValue: fractureDefaultValues.anyOtherFractureAmount * units,
      },
    ];
  };

  //paths of unit fields to update based on accident facility rider Unit
  const unitFieldsToUpdate = [
    { path: propertyPaths.unitsOfAccidentFacilityCare },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfAccidentInjuries },
    { path: accidentBenefitsUpdateUnitsPath.followUpTreatmentDetails.units },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfAccAmbulanceUrgentCare },
    { path: accidentBenefitsUpdateUnitsPath.xRayDetails.units },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfEmergencyRoomServices },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfAccAccidentSurgeries },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfDiagnosticPainManagement },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfTreatmentSupportAndRecovery },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfAccidentalDeathAndDismemberment },
    { path: accidentBenefitsUpdateUnitsPath.unitsOfDislocationRider },
  ];

  const accBenefitUpdate = () => {
    let pathValues = [
      //Accident Facility care
      {
        path: propertyPaths.hospitalAdmissionDetails.benefitAmount,
        benefit: 'ACCHospitalAdmissionBenefit.standardBenefitAmount',
      },
      {
        path: propertyPaths.intensiveCareUnitConfinementDetails.benefitAmount,
        benefit: 'ACCIntensiveCareUnitConfinementBenefit.standardBenefitAmount',
      },
      {
        path: propertyPaths.hospitalConfinementDetails.benefitAmount,
        benefit: 'ACCHospitalConfinementBenefit.standardBenefitAmount',
      },
      {
        path: propertyPaths.stepDownIntensiveCareUnitConfinementDetails.benefitAmount,
        benefit: 'ACCStepDownIntensiveCareUnitConfinementBenefit.standardBenefitAmount',
      },
      {
        path: propertyPaths.rehabilitationFacilityDetails.benefitAmount,
        benefit: 'ACCRehabilitationFacilityBenefit.standardBenefitAmount',
      },

      //Accident Injuries
      {
        path: accidentBenefitsUpdateUnitsPath.brainInjuryDiagnosisDetailsBenefitAmount,
        benefit: 'ACCBrainInjuryDiagnosisDetails.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.brokenToothDetailsBenefitAmount,
        benefit: 'ACCBrokenToothBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.comaDetailsBenefitAmount,
        benefit: 'ACCComaBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.punctureWoundDetailsBenefitAmount,
        benefit: 'ACCPunctureWoundBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.gunshotWoundDetailsBenefitAmount,
        benefit: 'ACCGunshotWoundBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.threeOrFourLimbsAmount,
        benefit: 'ACCParalysisBenefit.standardBenefitAmount',
      },
      //Accident Treatment and Urgent Care - Follow Up Treatments
      {
        path: accidentBenefitsUpdateUnitsPath.followUpTreatmentDetailsBenefitAmount,
        benefit: 'ACCFollowUpTreatmentBenefit.standartBenefitAmount',
      },
      //Accident Treatment and Urgent Care - Ambulance Urgent Care
      {
        path: accidentBenefitsUpdateUnitsPath.ambulanceUrgentCareBenefitBenefitAmount,
        benefit: 'ACCAmbulanceUrgentCareBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.ambulanceAirDetailsBenefitAmount,
        benefit: 'ACCAmbulanceAirBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.ambulanceGroundOrWaterDetailsBenefitAmount,
        benefit: 'ACCAmbulanceGroundOrWaterBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.urgentCareDetailsBenefitAmount,
        benefit: 'ACCUrgentCareBenefit.standardBenefitAmount',
      },

      //Accident Treatment and Urgent Care - X-ray
      {
        path: accidentBenefitsUpdateUnitsPath.XRaybenefitAmount,
        benefit: 'ACCXRayBenefit.standardBenefitAmount',
      },

      //Accident Treatment and Urgent Care - Emergency Room Services
      {
        path: accidentBenefitsUpdateUnitsPath.emergencyRoomServicesDetailsBenefitAmount,
        benefit: 'ACCEmergencyRoomServicesBenefit.standardBenefitAmount',
      },

      {
        path: accidentBenefitsUpdateUnitsPath.accidentalDeathDetailsBenefitAmount,
        benefit: 'ACCAccidentalDeathBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.accidentalDeathCommonCarrierDetailsBenefitAmount,
        benefit: 'ACCAccidentalDeathCommonCarrierBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.bothArmsOrHandsLossAmount,
        benefit: 'ACCAccidentalDismembermentBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.bothLegsOrFeetLossAmount,
        benefit: 'ACCAccidentalDismembermentBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.oneHandOrArmAndOneLegOrFootLossAmount,
        benefit: 'ACCAccidentalDismembermentBenefit.standardBenefitAmount',
      },
      //Accident Treatment and Urgent Care - Accident Surgeries
      {
        path: accidentBenefitsUpdateUnitsPath.miscellaneousOutpatientSurgeryDetailsBenefitAmount,
        benefit: 'ACCMiscellaneousOutpatientSurgeryBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.tendonLigamentRotatorCuffDetailsWithRepairAmount,
        benefit: 'ACCTendonLigamentRotatorCuffBenefit.withRepairStandardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.tendonLigamentRotatorCuffDetailsWithoutRepairAmount,
        benefit: 'ACCTendonLigamentRotatorCuffBenefit.withoutRepairStandardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.generalAnesthesiaDetailsBenefitAmount,
        benefit: 'ACCGeneralAnesthesiaBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.rupturedDiscDetailsBenefitAmount,
        benefit: 'ACCRupturedDiscBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.bloodPlasmaPlateletsDetailsBenefitAmount,
        benefit: 'ACCBloodPlasmaPlateletsBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.eyeInjuryDetailsBenefitAmount,
        benefit: 'ACCEyeInjuryBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.companionNonLocalLodgingDetailsBenefitAmount,
        benefit: 'ACCCompanionNonLocalLodgingBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.medicalEquipmentDetailsBenefitAmount,
        benefit: 'ACCMedicalEquipmentBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.medicalSuppliesDetailsBenefitAmount,
        benefit: 'ACCMedicalSuppliesBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.medicationDetailsBenefitAmount,
        benefit: 'ACCMedicationBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.nonLocalTransportationDetailsBenefitAmount,
        benefit: 'ACCNonLocalTransportationBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.postAccidentCommonCarrierTransportationDetailsBenefitAmount,
        benefit: 'ACCPostAccidentCommonCarrierTransportationBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.oneDeviceAmount,
        benefit: 'ACCProsthesisBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.residenceAutomobileModificationDetailsBenefitAmount,
        benefit: 'ACCResidenceAutomobileModificationBenefit.standardBenefitAmount',
      },
      //Accidental Pain Management
      {
        path: accidentBenefitsUpdateUnitsPath.painManagementDetailsBenefitAmount,
        benefit: 'ACCPainManagementBenefit.standardBenefitAmount',
      },
      {
        path: accidentBenefitsUpdateUnitsPath.painManagementCorticosteroidInjectionBenefitAmount,
        benefit: 'ACCPainManagementCorticosteroidInjectionBenefit.standardBenefitAmount',
      },

      {
        path: accidentBenefitsUpdateUnitsPath.automobileAccidentalDeathDetailsBenefitAmount,
        benefit: 'ACCAutomobileAccidentalDeathBenefit.standardBenefitAmount',
      },
    ];
    if (situsStateCd !== 'KS') {
      pathValues.push(
        {
          path: accidentBenefitsUpdateUnitsPath.therapyDetailsBenefitAmount,
          benefit: 'ACCTherapyBenefit.standardBenefitAmount',
        })
    }
    return pathValues;
  }

  const majorDiagnosticBenefitAmt = getBenefitAmount(
    defaultPlanValues,
    'ACCMajorDiagnosticExamBenefit.standardBenefitAmount'
  );

  // Diagnostic exam updates
  const diagnosticPaths = [
    {
      path: accidentBenefitsUpdateUnitsPath.openAbdominalOrThoracicSurgeryDetailsBenefitAmount,
      calc: openAbdominalOrThoracicSurgeryDetailsBenefitAmount,
    },
    //
    {
      path: accidentBenefitsUpdateUnitsPath.majorDiagnosticExamDetails.cATBenefitAmount,
      calc: majorDiagnosticBenefitAmt,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.majorDiagnosticExamDetails.eEGBenefitAmount,
      calc: majorDiagnosticBenefitAmt,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.majorDiagnosticExamDetails.mRIBenefitAmount,
      calc: majorDiagnosticBenefitAmt,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.majorDiagnosticExamDetails.pETBenefitAmount,
      calc: majorDiagnosticBenefitAmt,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.majorDiagnosticExamDetails.ultrasoundBenefitAmount,
      calc: majorDiagnosticBenefitAmt,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.twoOrMoreDevicesAmount,
      calc: getBenefitAmount(defaultPlanValues, 'ACCProsthesisBenefit.standardBenefitAmount') * 2,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.withoutRepairAmount,
      calc: lacerationBenefit * 0.2,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.lacerationLength3Amount,
      calc: lacerationBenefit,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.lacerationLength1Amount,
      calc: lacerationBenefit * 0.2,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.lacerationLength2Amount,
      calc: lacerationBenefit * 0.5,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.burnDegreeDetails[0].burnSize1Amount,
      calc: burnDegreeBenefitAmount * 0.0125,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.burnDegreeDetails[0].burnSize2Amount,
      calc: burnDegreeBenefitAmount * 0.025,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.burnDegreeDetails[0].burnSize3Amount,
      calc: burnDegreeBenefitAmount * 0.125,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.burnDegreeDetails[1].burnSize1Amount,
      calc: burnDegreeBenefitAmount * 0.05,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.burnDegreeDetails[1].burnSize2Amount,
      calc: burnDegreeBenefitAmount * 0.25,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.burnDegreeDetails[1].burnSize3Amount,
      calc: burnDegreeBenefitAmount,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.legOrFootLossAmount,
      calc: accidentalDismembermentBenefit * 0.5,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.oneOrMoreFingersLossAmount,
      calc: accidentalDismembermentBenefit * (situsStateCd === 'NH' ? 0.05 : 0.1),
    },
    {
      path: accidentBenefitsUpdateUnitsPath.oneOrMoreToesLossAmount,
      calc: accidentalDismembermentBenefit * (situsStateCd === 'NH' ? 0.05 : 0.1),
    },
    {
      path: accidentBenefitsUpdateUnitsPath.maximumBenefitAmount,
      calc: accidentalDismembermentBenefit,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.armOrHandLossAmount,
      calc: accidentalDismembermentBenefit * 0.5,
    },

    //Functional loss

    {
      path: accidentBenefitsUpdateUnitsPath.hearingLossAmount,
      calc: functionalLossBenefitAmount * 0.5,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.sightLossAmount,
      calc: functionalLossBenefitAmount * 0.5,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.speechLossAmount,
      calc: functionalLossBenefitAmount,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.functionalLossDetailsMaximumBenefitAmount,
      calc: functionalLossBenefitAmount,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.openAbdominalOrThoracicSurgeryDetailsBenefitAmount,
      calc: openAbdominalOrThoracicSurgeryDetailsBenefitAmount,
    },
    {
      path: accidentBenefitsUpdateUnitsPath.oneOrTwoLimbsAmount,
      calc: paralysisBenefitAmount * 0.5,
    },
  ];

  const updateAllUnitsAndBenefitAmounts = (facilityUnits) => {
    if (!isNaN(Number(facilityUnits))) {
      const units = Number(facilityUnits);
      const benefitUnitFieldsToUpdate = unitFieldsToUpdate.map(value => ({
        path: value.path,
        newValue: units,
      }));

      const accFacilityBenefitAmountUpdate = (path, units, benefitName) => ({
        path,
        newValue: units * getBenefitAmount(defaultPlanValues, benefitName),
      });

      const createStaticBenefitUpdate = (path, units, amount) => ({
        path,
        newValue: units * amount,
      });

      const accBenefitUpdates = accBenefitUpdate().map(({ path, benefit }) =>
        accFacilityBenefitAmountUpdate(path, units, benefit)
      );

      const staticBenefitValueUpdates = diagnosticPaths.map(
        ({ path, calc }) => createStaticBenefitUpdate(path, units, calc)
      );

      const {
        maximumClosedReductionBenefitAmount,
        maximumOpenReductionBenefitAmount,
      } = getCalculatedBenefitAmounts(units);

      updateQuoteFieldValues([
        ...benefitUnitFieldsToUpdate,
        ...accBenefitUpdates,
        ...staticBenefitValueUpdates,
        {
          path: accidentBenefitsUpdateUnitsPath.maximumClosedReductionBenefitAmount,
          newValue: maximumClosedReductionBenefitAmount?.amount,
        },
        {
          path: accidentBenefitsUpdateUnitsPath.maximumOpenReductionBenefitAmount,
          newValue: maximumOpenReductionBenefitAmount?.amount,
        },
        ...dislocationFieldUpdate(units),
        ...fractureDislocationFieldUpdate(units),
      ]);

    }
  }

  useEffect(() => {
    setPlanUnitsOptions(handleRestrictPlanOptions(numOfPlans, getQuoteFieldValue));
    const units = Number(getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare));
    updateAllUnitsAndBenefitAmounts(units);
  }, []);

  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'accident-facility-care-units-row-1' },
      items: [
        {
          key: 'units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentFacilityCare)}
              onChange={e => {
                updateAllUnitsAndBenefitAmounts(e.target.value);
              }}
              id={propertyPaths.unitsOfAccidentFacilityCare}
              required
              options={planUnitsOptions}
            />
          ),
          props: { xs: 6, sm: 6, md: 6 },
        },
      ],
    },
  ];

  const accidentFacilityCare: ConditionalSection[] = [
    {
      Component: InitialHospitalAdmission,
      key: 'InitialHospitalAdmission',
      label: 'INITIAL HOSPITAL ADMISSION',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isHospitalAdmissionApplied),
      disabled: true,
    },
    {
      Component: DailyHospitalConfinement,
      key: 'DailyHospitalConfinement',
      label: 'DAILY HOSPITAL CONFINEMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isHospitalConfinementApplied),
    },
    {
      Component: IntensiveCareUnitConfinement,
      key: 'IntensiveCareUnitConfinement',
      label: 'INTENSIVE CARE UNIT CONFINEMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isIntensiveCareUnitConfinementApplied),
    },
    {
      Component: StepDownICUConfinement,
      key: 'StepDownICUConfinement',
      disabled: true,
      hidden: true,
      label: 'STEP DOWN INTENSIVE CARE UNIT CONFINEMENT',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isStepDownIntensiveCareUnitConfinementApplied),
    },
    {
      Component: RehabilitationFacility,
      key: 'RehabilitationFacility',
      disabled: true,
      label: 'REHABILITATION FACILITY',
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isRehabilitationFacilityApplied),
    },
  ];

  return (
    <FormWrapper title="Accident Facility Care" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={accidentFacilityCare} />
      </Grid>
    </FormWrapper>
  );
};

export const formatCurrencyValue = (value: string | number | undefined): string => {
  if (!value) return '0.00';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '0.00';

  return numValue.toFixed(2);
};

export const AccidentInjuriesBlock = () => {
  const { getQuoteFieldValue } = useQuoteField();
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentInjuriesPath(selectedPlan?.planIndex ?? 0, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'accident-injury-units-row-1' },
      items: [
        {
          key: 'units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              type="number"
              options={unitsOptions}
              value={getQuoteFieldValue<number>(propertyPaths.unitsOfAccidentInjuries)}
              id={propertyPaths.unitsOfAccidentInjuries}
              disabled
              required
            />
          ),
          props: { xs: 6, sm: 6, md: 6 },
        },
      ],
    },
  ];
  const accidentInjuries: ConditionalSection[] = [
    {
      Component: BrainInjuryDiagnosis,
      key: 'BrainInjuryDiagnosis',
      label: 'BRAIN INJURY DIAGNOSIS',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isBrainInjuryDiagnosisApplied),
    },
    {
      Component: BrokenTooth,
      key: 'BrokenTooth',
      label: 'BROKEN TOOTH',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isBrokenToothApplied),
    },
    {
      Component: BurnDetails,
      key: 'BurnDetails',
      label: 'BURN',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isBurnApplied),
    },
    {
      Component: BurnSkinGraft,
      key: 'BurnSkinGraft',
      label: 'BURN SKIN GRAFT',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isBurnSkinGraftApplied),
    },
    {
      Component: ComaDetails,
      key: 'ComaDetails',
      label: 'COMA',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isComaApplied),
    },
    {
      Component: LacerationDetails,
      key: 'LacerationDetails',
      label: 'LACERATION',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isLacerationApplied),
    },
    {
      Component: ParalysisDetails,
      key: 'ParalysisDetails',
      label: 'PARALYSIS',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isParalysisApplied),
    },
    {
      Component: PunctureWound,
      key: 'PunctureWound',
      label: 'PUNCTURE WOUND',
      disabled: true,
      hidden: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isPunctureWoundApplied),
    },
    {
      Component: GunshotWound,
      key: 'GunshotWound',
      label: 'GUNSHOT WOUND',
      disabled: true,
      hidden: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isGunshotWoundApplied),
    },
  ];

  return (
    <FormWrapper title="Accident Injuries" collapsible withSeparator>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
        <QuoteFormConditionalSection sections={accidentInjuries} />
      </Grid>
    </FormWrapper>
  );
};

export const AccidentTreatmentAndUrgentCareFollowUpBlock = () => {
  const { getQuoteFieldValue } = useQuoteField();
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = accidentFollowupTreatmentPath(selectedPlan?.planIndex ?? 0, 0);

  const accidentTreatmentAndUrgentCareFollowUp: ConditionalSection[] = [
    {
      Component: AccidentFollowUpTreatment,
      key: 'AccidentFollowUpTreatment',
      label: 'ACCIDENT FOLLOW UP TREATMENT',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isFollowUpTreatmentApplied),
    },
  ];

  return (
    <FormWrapper title="Accident Treatment and Urgent Care - Follow Up Treatment" collapsible withSeparator>
      <Grid container alignItems="center">
        <QuoteFormConditionalSection sections={accidentTreatmentAndUrgentCareFollowUp} />
      </Grid>
    </FormWrapper>
  );
};

const AccidentXray = () => {
  const { getQuoteFieldValue } = useQuoteField();
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getAccidentXRayPath(selectedPlan?.planIndex ?? 0, 0);

  const rows: GridRowProps[] = [
    {
      rowProps: { key: 'accident-treatment-xray-row-1' },
      items: [
        {
          key: 'accident-treatment-xray-units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Units"
              options={unitsOptions}
              type="number"
              required
              disabled
              value={getQuoteFieldValue<number>(propertyPaths.xRayDetails.units)}
              id={propertyPaths.xRayDetails.units}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'accident-treatment-xray-row-2' },
      items: [
        {
          key: 'accident-treatment-xray-incurral-period',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Incurral Period (Days)"
              type="number"
              value={getQuoteFieldValue<number>(propertyPaths.xRayDetails.incurralPeriod)}
              disabled
              required
              id={propertyPaths.xRayDetails.incurralPeriod}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: 'accident-treatment-xray-customize-benefit',
          hidden: true,
          element: (
            <QuoteFormControlElement
              controlType="checkbox"
              label="Customize Benefit Amount"
              disabled
              checked={getQuoteFieldValue<boolean>(propertyPaths.xRayDetails.customizeBenefitAmount)}
              id={propertyPaths.xRayDetails.customizeBenefitAmount}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'accident-treatment-xray-row-3' },
      items: [
        {
          key: 'accident-treatment-xray-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              adornment="money"
              required
              value={formatCurrencyValue(getQuoteFieldValue<number>(propertyPaths.xRayDetails.benefitAmount))}
              disabled
              id={propertyPaths.xRayDetails.benefitAmount}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
        {
          key: 'accident-treatment-xray-benefit-times',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              label="Number of Times Benefit is Payable per Time Period"
              searchKey={`ACCXRayBenefit.maxBenefitNumber`}
              value={getQuoteFieldValue<number>(propertyPaths.xRayDetails.maxBenefitNumber)}
              disabled
              required
              id={propertyPaths.xRayDetails.maxBenefitNumber}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
    {
      rowProps: { key: 'accident-treatment-xray-row-4' },
      items: [
        {
          key: 'accident-treatment-xray-time-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`ACCXRayBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<number>(propertyPaths.xRayDetails.timePeriodCd)}
              disabled
              required
              id={propertyPaths.xRayDetails.timePeriodCd}
            />
          ),
          props: { xs: 12, sm: 6, md: 6 },
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AccidentTreatmentAndUrgentCareXRayBlock = () => {
  const { getQuoteFieldValue } = useQuoteField();
  const { selectedPlan } = useSelector((state: ReducerState) => state.quoteForm);

  const propertyPaths = getAccidentXRayPath(selectedPlan?.planIndex ?? 0, 0);

  const accidentTreatmentAndUrgentCareXray: ConditionalSection[] = [
    {
      Component: AccidentXray,
      key: 'AccidentXray',
      label: 'X-RAY',
      disabled: true,
      isApplied: getQuoteFieldValue<boolean>(propertyPaths.isXRayApplied),
    },
  ];

  return (
    <FormWrapper title="Accident Treatment and Urgent Care - X-ray" collapsible withSeparator>
      <Grid container alignItems="center">
        <QuoteFormConditionalSection sections={accidentTreatmentAndUrgentCareXray} />
      </Grid>
    </FormWrapper>
  );
};
